<template>
  <div class="reset-password-container">
    <div class="reset-password-form">
      <div v-if="loading" class="loading-state">
        <p>{{ $t('legacy.Проверка ссылки...') }}</p>
      </div>
      
      <div v-else-if="tokenError" class="error-state">
        <h1>{{ $t('common.error') }}</h1>
        <p class="error-description">{{ tokenError }}</p>
        <router-link :to="authPath('/forgot-password')" class="link-button">{{ $t('Запросить новую ссылку') }}</router-link>
      </div>
      
      <div v-else-if="success" class="success-state">
        <h1>{{ $t('Пароль изменён') }}</h1>
        <p class="success-description">{{ $t('Ваш пароль успешно изменён. Вы будете перенаправлены в личный кабинет...') }}</p>
      </div>
      
      <div v-else>
        <h1>{{ $t('legacy.Новый пароль') }}</h1>
        <p class="reset-description">{{ $t('Введите новый пароль для вашего аккаунта') }}</p>
        <form @submit.prevent="handleResetPassword">
          <div class="form-group password-field" :class="{ 'error-from-group': fieldErrors.password }">
            <input
              :type="showPassword ? 'text' : 'password'"
              id="password"
              v-model="form.password"
              :placeholder="$t('legacy.Новый пароль')"
              class="input-field"
            />
            <span class="password-toggle" @click="showPassword = !showPassword">
              <span v-if="showPassword" class="icon-eye">👁</span>
              <span v-else class="icon-eye-off">👁‍🗨</span>
            </span>
            <p v-if="fieldErrors.password" class="input-error">{{ fieldErrors.password }}</p>
          </div>
          <div class="form-group password-field" :class="{ 'error-from-group': fieldErrors.confirmPassword }">
            <input
              :type="showConfirmPassword ? 'text' : 'password'"
              id="confirmPassword"
              v-model="form.confirmPassword"
              :placeholder="$t('legacy.Подтвердите пароль')"
              class="input-field"
            />
            <span class="password-toggle" @click="showConfirmPassword = !showConfirmPassword">
              <span v-if="showConfirmPassword" class="icon-eye">👁</span>
              <span v-else class="icon-eye-off">👁‍🗨</span>
            </span>
            <p v-if="fieldErrors.confirmPassword" class="input-error">{{ fieldErrors.confirmPassword }}</p>
          </div>
          <button type="submit" class="submit-button" :disabled="submitting">
             {{ submitting ? $t('legacy.Сохранение...') : $t('legacy.Сохранить пароль') }}
          </button>
        </form>
        <div class="reset-info">
          <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { validateResetToken, resetPassword } from '@/api/auth';
import { localizedAuthPath } from '@/utils/authRoutes';

export default {
  data() {
    return {
      form: {
        password: '',
        confirmPassword: ''
      },
      token: '',
      loading: true,
      submitting: false,
      success: false,
      tokenError: '',
      errorMessage: '',
      fieldErrors: {},
      showPassword: false,
      showConfirmPassword: false
    };
  },
  async created() {
    this.token = this.$route.params.token;
    if (!this.token) {
      this.tokenError = this.$t('legacy.Недействительная ссылка для сброса пароля');
      this.loading = false;
      return;
    }
    
    try {
      const response = await validateResetToken(this.token);
      if (!response.success) {
         this.tokenError = this.translateBackendError(response, 'legacy.Недействительная ссылка');
      }
    } catch (error) {
      this.tokenError = this.translateBackendError(
        error.response?.data,
        'legacy.Недействительная ссылка для сброса пароля'
      );
    } finally {
      this.loading = false;
    }
  },
  methods: {
    authPath(path) {
      return localizedAuthPath(this.$route, path);
    },
    translateBackendError(response, fallback) {
      const errors = response?.errors || {};
      const detail = errors.details?.general?.[0]
        || (Array.isArray(errors.details) ? errors.details[0] : errors.details);
      return this.$t(errors.title || detail || fallback);
    },
    async handleResetPassword() {
      this.fieldErrors = {};
      this.errorMessage = '';
      
      if (!this.form.password) {
        this.fieldErrors.password = this.$t('legacy.Введите пароль');
        return;
      }
      
      if (this.form.password.length < 6) {
        this.fieldErrors.password = this.$t('legacy.Пароль должен быть не менее 6 символов');
        return;
      }
      
      if (this.form.password !== this.form.confirmPassword) {
        this.fieldErrors.confirmPassword = this.$t('legacy.Пароли не совпадают');
        return;
      }
      
      this.submitting = true;
      
      try {
        const response = await resetPassword(this.token, this.form.password);
        
        if (response.success) {
          this.success = true;
          
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('userToken', response.result.accessToken);
          localStorage.setItem('user', JSON.stringify(response.result.user));
          
          this.$store.commit('auth/SET_USER', response.result.user);
          await this.$store.dispatch('subscription/clear');
          await this.$store.dispatch('subscription/init');
          
          setTimeout(() => {
            this.$router.push('/projects');
          }, 2000);
        } else {
          this.errorMessage = this.translateBackendError(response, 'legacy.Произошла ошибка');
        }
      } catch (error) {
        this.errorMessage = this.translateBackendError(
          error.response?.data,
          'legacy.Произошла ошибка при сбросе пароля'
        );
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.reset-password-container {
  background: linear-gradient(to bottom, #FAF9F7, #BBCAD8);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.reset-password-form {
  width: 100%;
  max-width: 420px;
  padding: 20px;
}

.loading-state,
.error-state,
.success-state {
  text-align: center;
}

.error-state h1,
.success-state h1 {
  margin-bottom: 20px;
}

.error-description,
.success-description {
  font-family: 'Montserrat';
  font-weight: 200;
  margin-bottom: 30px;
}

.link-button {
  display: inline-block;
  padding: 14px 30px;
  background-color: #BBCAD8;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-family: 'Montserrat';
  transition: background-color 0.3s;
}

.link-button:hover {
  background-color: #496782;
}

h1 {
  margin-bottom: 15px;
  font-family: 'Montserrat';
  font-weight: 200;
  text-align: center;
}

.reset-description {
  text-align: center;
  font-family: 'Montserrat';
  font-weight: 200;
  margin-bottom: 40px;
}

.form-group {
  height: 50px;
  margin-top: 15px;
  position: relative;
}

.input-field {
  height: 50px;
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-sizing: border-box;
  box-shadow: 1px 1px 4px 0px #12223733 inset;
  font-family: 'Montserrat';
  font-weight: 200;
}

::placeholder {
  color: #BBCAD8;
  opacity: 1;
}

.password-field {
  position: relative;
}

.password-toggle {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  right: 15px;
}

.submit-button {
  width: 100%;
  padding: 14px;
  font-size: 16px;
  background-color: #BBCAD8;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s;
  margin-top: 40px;
}

.submit-button:hover:not(:disabled) {
  background-color: #496782;
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.reset-info {
  text-align: center;
  margin-top: 20px;
}

.error {
  color: red;
  margin-top: 10px;
}

.input-error {
  color: #000;
  background-color: #fff;
  box-shadow: 0px 0px 10px 0px #B2B0AD66;
  padding: 10px;
  border-radius: 10px;
  position: absolute;
  right: 0;
  z-index: 1;
  top: 55px;
  font-size: 12px;
}

.form-group.error-from-group::after {
  content: '!';
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  width: 16px;
  height: 16px;
  background-color: #B60000;
  color: #FFFFFF;
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
  right: 45px;
  top: 17px;
}
</style>
